import { useActionMutation } from '../useMutation/index';
import type { DocumentNode } from 'graphql'
import isEmpty from 'lodash/isEmpty'

export type UseZendeskDuplicateHandlersOptions<TEntity> = {
  /**
   * Callback when duplicates are detected after save
   */
  onDuplicatesDetected?: (duplicates: TEntity[]) => void

  /**
   * GraphQL query/mutation for checking duplicates
   */
  query: DocumentNode
}

export type UseZendeskDuplicateHandlersResult<TEntity> = {
  /**
   * Handler for checking duplicate Zendesk tickets
   * @param ticketNo - The Zendesk ticket number to check
   * @param excludeEntityId - Optional entity ID to exclude from results
   * @returns Array of duplicate entities
   */
  handleCheckDuplicates: (ticketNo: string, excludeEntityId?: string) => Promise<TEntity[]>

  /**
   * Handler for when duplicates are detected
   * @param duplicates - Array of duplicate entities
   */
  handleDuplicatesDetected: (duplicates: TEntity[]) => void
}

/**
 * Generic hook that provides handlers for Zendesk duplicate detection.
 * Works with any entity type (Order, PurchaseOrder, etc.) that has Zendesk ticket tracking.
 *
 * This hook combines:
 * - GraphQL mutation for checking duplicate tickets
 * - Handler functions for duplicate checking and notification
 *
 * @template TEntity - The entity type (Order, PurchaseOrder, etc.)
 * @template TArgs - The GraphQL args type for the check duplicates mutation
 *
 * @example
 * ```tsx
 * const toast = useToast()
 * const { handleCheckDuplicates, handleDuplicatesDetected } = useZendeskDuplicateHandlers<Order>({
 *   query: checkOrderZendeskTicketDuplicatesQuery,
 *   onDuplicatesDetected: (duplicates) => {
 *     toast.showWarning({
 *       title: 'Duplicate Ticket Warning',
 *       message: `Found ${duplicates.length} duplicate(s)`
 *     })
 *   }
 * })
 *
 * <ConversationIdInlineInput
 *   onCheckDuplicates={handleCheckDuplicates}
 *   onDuplicatesDetected={handleDuplicatesDetected}
 * />
 * ```
 */
export const useZendeskDuplicateHandlers = <TEntity = any, TArgs extends object = any>(
  options: UseZendeskDuplicateHandlersOptions<TEntity>
): UseZendeskDuplicateHandlersResult<TEntity> => {
  const { onDuplicatesDetected, query } = options

  // GraphQL mutation for checking duplicates
  const [onCheckDuplicates] = useActionMutation<TEntity[], TArgs>({
    query,
    options: {
      mutationOptions: {
        fetchPolicy: 'network-only', // Always fetch fresh data to ensure accuracy
      },
    },
  })

  const handleCheckDuplicates = async (ticketNo: string, excludeEntityId?: string): Promise<TEntity[]> => {
    try {
      // For unified duplicate check, we pass both excludeOrderId and excludePurchaseOrderId
      // The action will use the appropriate one based on the entity type being checked
      // If excludeEntityId is provided, it will be set for both fields and the action will filter accordingly
      const result = await onCheckDuplicates({
        ticketNo,
        excludeOrderId: excludeEntityId,
        excludePurchaseOrderId: excludeEntityId,
      } as TArgs)
      return result || []
    } catch (error) {
      // Fail gracefully - don't block user workflow
      console.error('Error checking for duplicate tickets:', error)
      return []
    }
  }

  const handleDuplicatesDetected = (duplicates: TEntity[]) => {
    if (isEmpty(duplicates)) return
    onDuplicatesDetected?.(duplicates)
  }

  return {
    handleCheckDuplicates,
    handleDuplicatesDetected,
  }
}
